import { type FocusEvent, memo, type MouseEvent, type RefObject } from 'react'
import { clsx } from 'clsx'

import css from './UserMenu.module.scss'

import type { AuthUser } from '@/lib/auth/AuthProvider'

import UserIcon from '@/assets/svgs/user.svg?react'

import type { ControllerProps } from './UserMenu.controller'

export type ViewProps = ControllerProps & {
  rootRef: RefObject<HTMLDivElement>
  user: AuthUser
  isOpen: boolean
  isSigningOut: boolean
  onMouseEnter: () => void
  onMouseLeave: (event: MouseEvent<HTMLDivElement>) => void
  onBlur: (event: FocusEvent<HTMLDivElement>) => void
  onToggle: () => void
  onEscape: () => void
  onProfile: (event: MouseEvent<HTMLAnchorElement>) => void
  onSignOut: () => void
}

const View = memo(function UserMenuView({
  rootRef,
  className,
  user,
  isOpen,
  isSigningOut,
  onMouseEnter,
  onMouseLeave,
  onBlur,
  onToggle,
  onEscape,
  onProfile,
  onSignOut
}: ViewProps) {
  return (
    <div
      ref={rootRef}
      className={clsx('UserMenu', css.root, className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onBlur={onBlur}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          onEscape()
        }
      }}
    >
      <button
        type="button"
        className={clsx(css.trigger, user.isAdmin ? css.triggerAdmin : css.triggerUser)}
        aria-label={`${isOpen ? 'Close' : 'Open'} account menu${user.isAdmin ? ' for an administrator' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <UserIcon aria-hidden="true" />
      </button>
      {isOpen && (
        <div className={css.menu} role="dialog" aria-label="Account menu">
          <p className={css.name}>{user.displayName}</p>
          <p className={css.email}>{user.email}</p>
          <p className={css.role}>{user.isAdmin ? 'Administrator' : 'User'}</p>
          <a href="/profile" className={css.profileLink} onClick={onProfile} aria-disabled={isSigningOut || undefined}>
            <UserIcon aria-hidden="true" />
            <span>Profile</span>
          </a>
          <button
            className={css.signOut}
            type="button"
            onClick={onSignOut}
            disabled={isSigningOut}
            aria-busy={isSigningOut || undefined}
          >
            {isSigningOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  )
})

View.displayName = 'UserMenu_View'

export default View
